import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { servicesAPI } from "@/lib/api";
import ServiceCard from "@/components/ServiceCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Wrench, Plus, Store, MapPin } from "lucide-react";

export default function ServicesBrowse() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all"); // all | offering | request
  const [category, setCategory] = useState("all");
  const [country, setCountry] = useState("");
  const [postal, setPostal] = useState("");

  useEffect(() => {
    servicesAPI.getCategories().then((r) => setCats(r.data?.categories || [])).catch(() => setCats([]));
  }, []);

  const catLabel = useMemo(() => {
    const map = Object.fromEntries(cats.map((c) => [c.slug, c.label]));
    return (slug) => map[slug] || slug;
  }, [cats]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 40 };
    if (search) params.search = search;
    if (type !== "all") params.service_type = type;
    if (category !== "all") params.category = category;
    if (country && postal.trim()) { params.country = country; params.postal_code = postal.trim(); }
    servicesAPI
      .getListings(params)
      .then((res) => setListings(res.data?.listings || []))
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, [search, type, category, country, postal]);

  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Car setups, rig builds, coaching, liveries, and more: hire specialists or post what you are looking for.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/storefront/edit")} data-testid="services-my-storefront-button" className="border-primary/50 text-primary hover:bg-primary/10">
            <Store className="mr-2 h-4 w-4" /> My storefront
          </Button>
          <Button onClick={() => navigate("/services/create")} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="services-create-button">
            <Plus className="mr-2 h-4 w-4" /> Service Offer/Request
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search services..."
              className="pl-9"
              data-testid="services-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-[150px]" data-testid="services-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All services</SelectItem>
                <SelectItem value="offering">Offering</SelectItem>
                <SelectItem value="request">Looking for</SelectItem>
              </SelectContent>
            </Select>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[180px]" data-testid="services-category-select">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {cats.map((c) => (
                  <SelectItem key={c.id} value={c.slug}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {/* Location filter */}
        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
          <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
            <MapPin className="h-4 w-4" /> Near me
          </span>
          <Select value={country || "none"} onValueChange={(v) => setCountry(v === "none" ? "" : v)}>
            <SelectTrigger className="w-[150px]" data-testid="services-country-select">
              <SelectValue placeholder="Country" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Any location</SelectItem>
              <SelectItem value="US">United States</SelectItem>
              <SelectItem value="CA">Canada</SelectItem>
            </SelectContent>
          </Select>
          {country && (
            <Input
              value={postal}
              onChange={(e) => setPostal(e.target.value)}
              placeholder={country === "CA" ? "Postal code (e.g. M5V)" : "ZIP code (e.g. 10001)"}
              className="w-[180px]"
              data-testid="services-postal-input"
            />
          )}
          {country && postal && (
            <button onClick={() => { setCountry(""); setPostal(""); }} className="text-xs text-muted-foreground underline" data-testid="services-clear-location">Clear</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[4/3] rounded-lg" />
          ))}
        </div>
      ) : listings.length ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="services-grid">
          {listings.map((l) => (
            <ServiceCard key={l.id} listing={l} categoryLabel={catLabel} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-20 text-center">
          <Wrench className="mb-3 h-12 w-12 text-muted-foreground" />
          <p className="font-medium">No services yet</p>
          <p className="text-sm text-muted-foreground">Be the first to offer a service or post what you need.</p>
          <Button className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate("/services/create")}>
            <Plus className="mr-2 h-4 w-4" /> Post a service
          </Button>
        </div>
      )}
    </div>
  );
}
